import { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { toast } from 'sonner';
import { RefreshCw, Download } from 'lucide-react';

/**
 * Componente PWA che gestisce:
 * - Registrazione del Service Worker
 * - Notifiche di aggiornamento disponibile
 * - Aggiornamento automatico dell'app
 */
export function PWAPrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      console.log('SW Registered:', registration);
      
      // Controlla aggiornamenti ogni ora
      if (registration) {
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error('SW registration error:', error);
    },
  });

  useEffect(() => {
    if (offlineReady) {
      toast.success('App pronta per l\'uso offline! 📱', {
        description: 'Puoi usare 99 Castelli anche senza connessione.',
        duration: 5000,
        icon: <Download className="h-5 w-5" />,
      });
      
      // Nascondi dopo 5 secondi
      setTimeout(() => setOfflineReady(false), 5000);
    }
  }, [offlineReady, setOfflineReady]);

  useEffect(() => {
    if (needRefresh) {
      toast('Nuova versione disponibile! 🎉', {
        description: 'Clicca per aggiornare l\'app.',
        duration: Infinity,
        icon: <RefreshCw className="h-5 w-5" />,
        action: {
          label: 'Aggiorna',
          onClick: () => {
            updateServiceWorker(true);
            setNeedRefresh(false);
          },
        },
        cancel: {
          label: 'Ignora',
          onClick: () => setNeedRefresh(false),
        },
      });
    }
  }, [needRefresh, setNeedRefresh, updateServiceWorker]);

  return null;
}

/**
 * Hook per verificare se l'app è installata come PWA
 */
export function useIsPWA(): boolean {
  if (typeof window === 'undefined') return false;
  
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  );
}

/**
 * Hook per il prompt di installazione PWA
 */
export function useInstallPrompt() {
  useEffect(() => {
    let deferredPrompt: any = null;

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPrompt = e;
      
      // Mostra il prompt di installazione dopo 30 secondi
      setTimeout(() => {
        if (deferredPrompt) {
          toast('Installa 99 Castelli! 🏰', {
            description: 'Aggiungi l\'app alla schermata home per un accesso rapido.',
            duration: 10000,
            icon: <Download className="h-5 w-5" />,
            action: {
              label: 'Installa',
              onClick: async () => {
                if (deferredPrompt) {
                  deferredPrompt.prompt();
                  const { outcome } = await deferredPrompt.userChoice;
                  console.log('Install prompt outcome:', outcome);
                  deferredPrompt = null;
                }
              },
            },
            cancel: {
              label: 'Non ora',
              onClick: () => {
                deferredPrompt = null;
              },
            },
          });
        }
      }, 30000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);
}
